'use strict';

define('vb/helpers/actionHelpers',[
  'vb/private/stateManagement/router',
  'vb/private/constants',
], (Router, Constants) => {
  /** @typedef {import('vb/action/action.js').default} Action */

  /** @type object */
  const container = Symbol('container');

  /**
   * A set of helper routines that require a context and are used by built-in actions.
   * The context is hidden using a closure.
   * @export
   * @category Action
   */
  class ActionHelpers {
    /**
     * @param {object} context
     */
    constructor(context) {
      if (!context) {
        return;
      }

      this[container] = context.container;
    }

    /**
     * Navigate to a page with input parameters.
     * @deprecated This action is deprecated in favor of 'navigate' action.
     *
     * @param {Object} options
     * @param {string} options.page - The destination page path.
     * @param {string} [options.flow] - The id of the destination flow. Change the content of the flow displayed
     * in the current page. When used in combination with a page property, navigates to the page in that flow.
     * @param {string} [options.application] - The id of the destination App UI. Change which App UI
     * is displayed in the host application. When used in combination with a page property, navigates
     * to the page in that App UI.
     * @param {'self'|'parent'} [options.target="self"] - The target of the destination for flow navigation.
     * This is only valid with the flow to change the content of the parent flow instead of the nested flow.
     * @param {Object} [options.params] - A map of URL parameters.
     * @param {'push'|'replace'|'skip'} [options.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @return {Promise<{navigated: boolean}>} a Promise that resolves to an object with a property named
     * 'navigated' which value is true or false depending on the outcome of the navigation.
     */
    navigateToPage(options) {
      return Router.queueNavigate(
        this[container],
        Object.assign(options, { operation: Constants.NavigateOperation.PAGE_OLD }),
      );
    }

    /**
     * Navigate action.
     *
     * @param {Object} options - The set of options for the navigation.
     * @param {string} [options.page] - The path to the destination page. The path is absolute starting
     * at the application or relative to the current page. When use in combination with flow or App UI,
     * the path cannot be absolute, and it navigates to the page relative to the flow or App UI.
     * @param {string} [options.flow] - The id of the destination flow. Change the content of the flow displayed
     * in the current page. When used in combination with a page property, navigates to the page in that flow.
     * @param {string} [options.application] - The id of the destination App UI. Change which App UI
     * is displayed in the host application. When used in combination with a page and flow property, navigates
     * to the page in that App UI.
     * @param {'self'|'parent'} [options.target="self"] - The target of the destination for flow navigation.
     * This is only valid with the flow to change the content of the parent flow instead of the nested flow.
     * @param {Object} [options.params] - A map of URL parameters. Same as in the navigateToPage
     * action
     * @param {'push'|'replace'|'skip'} [options.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @returns {Promise<{navigated: boolean}>} a promise that resolves to an object with a property named 'navigated'
     * which value is true if a navigation occurred.
     */
    navigate(options) {
      return Router.queueNavigate(this[container], options);
    }

    /**
     * Navigate back to the previous page in browser history with input parameters.
     *
     * @param {Object} [options] - The set of options for the navigateBack action
     * @param {Object} [options.params] - The input parameters for the page
     * the action is going back to
     * @returns {Promise<void>}
     */
    navigateBack(options) {
      // Pass the current page instance
      return Router.navigateBack(this[container], options);
    }
  }

  return ActionHelpers;
});

